import { HttpClient, HttpContext } from '@angular/common/http';
import { RECORD_HTTP_ERROR_WITHOUT_TOAST } from '../interceptors/error.interceptor';
import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { WorkspaceService } from './workspace.service';
import type { Call, Device } from '@twilio/voice-sdk';
import { SoftphoneStateDto, SoftphoneQualityTelemetryConfig, SOFTPHONE_CONTRACT_VERSION, SOFTPHONE_TERMINATION_REASON_STRENGTH, SoftphoneBrowserSessionResponseDto, SoftphoneConnectionStatus, SoftphoneIntegrationsInvalidatedDto, SoftphoneOriginNumberOptionDto, SoftphoneOriginPreferenceDto, SoftphoneOriginNumberMode, SoftphoneParticipantMutationResponseDto, SoftphoneParticipantSnapshotDto, SoftphoneTerminationReason, UpdateSoftphoneStatusRequestDto } from 'voxr-shared';
import {
  SoftphoneIncomingTransferSummary,
  SoftphoneMirroredConnectionStatus,
  SoftphoneMirroredRuntimeState,
  SoftphoneOwnerCommand,
  SoftphoneOwnerCommandResult,
  SoftphoneReleaseOwnershipOwnerCommand,
  SoftphoneSnapshotReason,
  SoftphoneSessionActiveCallSummary,
  SoftphoneSessionCoordinatorService
} from './softphone-session-coordinator.service';
import { SoftphoneBrowserSessionService } from './softphone-browser-session.service';
import { SoftphoneExceptionService } from './softphone-exception.service';
import { SocketService } from './socket.service';

declare global {
  interface Window {
    Twilio?: {
      Device: typeof Device;
    };
  }
}

export type SoftphoneState = 'inactive' | 'loading' | 'ready' | 'error';

export const SOFTPHONE_MICROPHONE_BLOCKED_DETAIL = 'Microphone blocked. Transfers will not ring until you allow the microphone for this site.';

/** Twilio reports 31401 (PermissionDeniedError) and 31402 (AcquisitionFailedError) after getUserMedia fails. */
export function getSoftphoneMicrophoneFailureKind(error: any): 'microphone-blocked' | 'microphone-unavailable' | undefined {
  const source = error?.cause || error;
  const code = Number(source?.code);
  if (code === 31401 || source?.name === 'PermissionDeniedError' || source?.name === 'NotAllowedError') return 'microphone-blocked';
  if (code === 31402 || ['AcquisitionFailedError', 'NotReadableError', 'NotFoundError'].includes(source?.name)) return 'microphone-unavailable';
  return undefined;
}

export type SoftphoneCallLeadIntent = {
  source: 'call-logs' | 'campaign-tasks';
  leadName?: string;
  leadPhone: string;
  leadState?: string;
  preferredFromNumber?: string;
  previousCallId?: string;
  contactId?: string;
  campaignId?: string;
};

export type SoftphoneFromNumberResolution = {
  requestedFromNumber?: string;
  resolvedFromNumber?: string;
  replacementFromNumber?: string;
  isAvailable: boolean;
  reason?: string;
};

export type SoftphonePrepareCallResponse = {
  softphoneQualityTelemetry?: SoftphoneQualityTelemetryConfig;
  callId: string;
  integrationId?: string;
  fromNumber?: string;
};

type SoftphoneResolvedIntegrationSummary = {
  integrationId: string;
  usage?: 'current' | 'draining';
  connectionStatus?: SoftphoneConnectionStatus;
};

type SoftphoneOwnedActiveCallPayload = {
  callId: string;
  integrationId: string;
  transferId?: string;
  callSid?: string;
  startedAt: string | Date;
  source?: 'call-logs' | 'campaign-tasks' | 'manual';
} | null;

export type ManagedIncomingSoftphoneCall = {
  call: Call;
  integrationId: string;
  device?: Device;
};

export type SoftphoneDeviceEvent =
  | { kind: 'registered'; integrationId: string; device: Device }
  | { kind: 'unregistered'; integrationId: string; device: Device }
  | { kind: 'error'; integrationId: string; device: Device; error: any };

export type SoftphoneCoachIncomingCall = ManagedIncomingSoftphoneCall & {
  consumerId: string;
};

export type CoachCorrelationStage =
  | 'intent-started'
  | 'device-acquired'
  | 'request-started'
  | 'coach-sid-received'
  | 'incoming-received'
  | 'incoming-matched'
  | 'incoming-mismatched'
  | 'request-failed'
  | 'intent-cleared';

type CoachCorrelationContext = {
  transferId?: string;
  callId?: string;
  coachCallSid?: string;
  integrationId?: string;
  consumerId?: string;
};

type CoachCorrelationDiagnostic = CoachCorrelationContext & {
  stage: CoachCorrelationStage;
  timestamp: string;
};

export type CoachAdmissionDiagnostic = {
  timestamp: string;
  reason?: string;
  transferId?: string;
  integrationId?: string;
  pendingTransferId?: string;
  hasActiveCall: boolean;
  isOwner: boolean;
  browserSessionMode?: string;
  softphoneState: SoftphoneState;
  runtimeState?: string;
  ownerLifecycleStatus?: string;
  integrationConnected: boolean;
};

type SoftphoneManagedRuntimeHandlers = {
  device: Device;
  registered: () => void;
  unregistered: () => void;
  incoming: (call: Call) => void;
  error: (error: any) => void;
  tokenWillExpire: () => void;
};

type SoftphoneManagedRuntime = {
  integrationId: string;
  usage: 'current' | 'draining';
  authorized: boolean;
  accountSid?: string;
  title?: string;
  device?: Device;
  status: 'connecting' | 'connected' | 'error' | 'disconnected' | 'retrying';
  deviceSid?: string;
  error?: string;
  lastSeenAt?: number;
  retryAt?: number;
  retryAttempt: number;
  expiresAt?: string;
  tokenLast4?: string;
  tokenRefreshTimeout?: ReturnType<typeof setTimeout>;
  retryTimeout?: ReturnType<typeof setTimeout>;
  handlers?: SoftphoneManagedRuntimeHandlers;
};

@Injectable({
  providedIn: 'root'
})
export class SoftphoneService {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private workspaceService = inject(WorkspaceService);
  private sessionCoordinator = inject(SoftphoneSessionCoordinatorService);
  private browserSession = inject(SoftphoneBrowserSessionService);
  private exceptions = inject(SoftphoneExceptionService);
  private socketService = inject(SocketService);

  private static twilioSdkPromise?: Promise<void>;
  private readonly selectedIntegrationStoragePrefix = 'voxr.softphone.selectedIntegration';
  private readonly incomingTransferTerminalTtlMs = 5 * 60_000;
  private readonly callSignalingReconnectMs = 30_000;
  private readonly completedIncomingTransfers: Array<{ keys: string[]; expiresAt: number }> = [];
  private sharedTwilioDevice?: Device;
  private sharedTwilioDeviceSubject = new BehaviorSubject<Device | undefined>(undefined);
  readonly selectedDevice$ = this.sharedTwilioDeviceSubject.asObservable();
  private softphoneStateSubject = new BehaviorSubject<SoftphoneState>('inactive');
  readonly softphoneState$ = this.softphoneStateSubject.asObservable();
  private softphoneStateDetailSubject = new BehaviorSubject<string | undefined>('Soft phone is off');
  readonly softphoneStateDetail$ = this.softphoneStateDetailSubject.asObservable();
  readonly sessionState$ = this.sessionCoordinator.runtimeState$;
  readonly ownership$ = this.sessionCoordinator.ownership$;
  readonly ownerCommand$ = this.sessionCoordinator.ownerCommand$;
  readonly ownerCommandResult$ = this.sessionCoordinator.ownerCommandResult$;
  readonly browserSessionMode$ = this.browserSession.mode$;
  readonly activeBrowser$ = this.browserSession.activeBrowser$;
  readonly browserSessionConflictRequested$ = this.browserSession.conflictRequested$;
  readonly browserSessionOwnershipLost$ = this.browserSession.ownershipLost$;
  private softphoneCallLeadIntentSubject = new Subject<SoftphoneCallLeadIntent>();
  readonly softphoneCallLeadRequested$ = this.softphoneCallLeadIntentSubject.asObservable();
  private softphonePanelOpenRequestSubject = new Subject<{ source: 'app-shell' | 'call-lead' | 'incoming-transfer'; }>();
  readonly softphonePanelOpenRequested$ = this.softphonePanelOpenRequestSubject.asObservable();
  private ownerIncomingCallSubject = new Subject<ManagedIncomingSoftphoneCall>();
  readonly ownerIncomingCall$ = this.ownerIncomingCallSubject.asObservable();
  private coachIncomingCallSubject = new Subject<SoftphoneCoachIncomingCall>();
  readonly coachIncomingCall$ = this.coachIncomingCallSubject.asObservable();
  private deviceEventSubject = new Subject<SoftphoneDeviceEvent>();
  readonly deviceEvent$ = this.deviceEventSubject.asObservable();
  private sharedDeviceWorkspaceId?: string;
  private sharedDeviceInitPromise?: Promise<Device | undefined>;
  private sharedDeviceInitPromiseGeneration?: number;
  private sharedDeviceConsumers = new Set<string>();
  private lastTokenFetchAt?: string;
  private tokenLast4?: string;
  private serverClockOffsetMs = 0;
  readonly selectedIntegrationId = signal<string | undefined>(undefined);
  readonly serverContractVersion = signal<number | undefined>(undefined);
  private activeCoachConsumerId?: string;
  private activeCoachCallSid?: string;
  private activeCoachContext?: CoachCorrelationContext;
  private recentCoachContext?: { context: CoachCorrelationContext; expiresAt: number; };
  private readonly coachCorrelationDiagnostics: CoachCorrelationDiagnostic[] = [];
  hasMoreIntegrations = false;
  private ownerActiveCallPayload: SoftphoneOwnedActiveCallPayload = null;
  private pendingTerminationObservation?: NonNullable<UpdateSoftphoneStatusRequestDto['terminationObservation']>;
  private scheduledTerminationObservation?: NonNullable<UpdateSoftphoneStatusRequestDto['terminationObservation']>;
  private runtimeSnapshotHeartbeat?: ReturnType<typeof setInterval>;
  private runtimeByIntegrationId = new Map<string, SoftphoneManagedRuntime>();
  private runtimeLifecycleStatus: 'starting' | 'active' | 'recovering' | 'releasing' | 'error' = 'active';
  private runtimeBootstrapStartedAt?: number;
  private runtimeLastSnapshotPublishedAt?: number;
  private runtimeLastSnapshotReason?: SoftphoneSnapshotReason;
  private runtimeSnapshotConfirmed = false;
  private runtimeSnapshotConfirmationFailed = false;
  private runtimeSnapshotQueue: Promise<void> = Promise.resolve();
  private runtimeSnapshotQueueGeneration = 0;
  private lastRuntimeSnapshotVersion = 0;
  private currentUserId?: string;
  private bootstrappedOwnerUserId?: string;
  private ownerBootstrapGeneration = 0;
  private ownerBootstrapInFlightGeneration?: number;
  private integrationReconciliation?: Promise<boolean>;
  private setupIssue?: SoftphoneStateDto['setupIssue'];
  private setupWorkspaceId?: string;
  private reconciliationRequested = false;
  private reconciliationRetryAttempt = 0;
  private reconciliationRetryTimeout?: ReturnType<typeof setTimeout>;

  private qualityTelemetry?: SoftphoneQualityTelemetryConfig;
  private qualityConfigReceivedAt = 0;
  receiveQualityTelemetry(value?: SoftphoneQualityTelemetryConfig) {
    if (!value || typeof value.enabled !== 'boolean' || !Number.isFinite(Date.parse(value.serverTime))
      || (value.enabled && (typeof value.stopAt !== 'string' || !Number.isFinite(Date.parse(value.stopAt))))) return;
    if (this.qualityTelemetry && Date.parse(value.serverTime) < Date.parse(this.qualityTelemetry.serverTime)) return;
    this.qualityTelemetry = { ...value }; this.qualityConfigReceivedAt = performance.now();
  }
  get qualityTelemetryEnabled() {
    const config = this.qualityTelemetry;
    return !!config?.enabled && !!config.stopAt
      && Date.parse(config.stopAt) > Date.parse(config.serverTime) + performance.now() - this.qualityConfigReceivedAt;
  }

  constructor() {
    this.browserSession.contractVersion$.subscribe(version => this.observeServerContractVersion(version));
    this.socketService.onEvent$<SoftphoneIntegrationsInvalidatedDto>('softphone-integrations-invalidated')
      .subscribe(() => this.requestIntegrationReconciliation());

    this.userService.user$.subscribe(user => {
      const nextUserId = user?._id?.toString();
      if (this.currentUserId === nextUserId) return;
      this.currentUserId = nextUserId;
      this.setupIssue = undefined;
      this.sessionCoordinator.publishRuntimeState({ setupIssue: undefined });
      this.qualityTelemetry = undefined;
      this.ownerBootstrapGeneration += 1;
      this.bootstrappedOwnerUserId = undefined;
      this.runtimeBootstrapStartedAt = undefined;
      this.runtimeLastSnapshotPublishedAt = undefined;
      this.runtimeLastSnapshotReason = undefined;
      this.runtimeSnapshotConfirmed = false;
      this.runtimeSnapshotConfirmationFailed = false;
      this.runtimeSnapshotQueueGeneration++;
      this.runtimeSnapshotQueue = Promise.resolve();
      this.cancelIntegrationReconciliationRetry();
      this.scheduledTerminationObservation = undefined;
      this.lastRuntimeSnapshotVersion = 0;
      this.completedIncomingTransfers.length = 0;

      if (!nextUserId) {
        this.hasMoreIntegrations = false;
        this.applySelectedIntegrationId(undefined);
        this.ownerActiveCallPayload = null;
        this.pendingTerminationObservation = undefined;
        void this.destroySharedSoftphoneDevice(false);
        this.publishSoftphoneState('inactive', 'Soft phone is off');
        return;
      }

      if (this.sessionCoordinator.isOwner) {
        void this.activateOwnerBrowserSession();
      }
    });

    this.workspaceService.workspace$.subscribe(workspace => {
      const workspaceId = workspace?._id?.toString();
      const workspaceChanged = workspaceId !== this.setupWorkspaceId;
      if (workspaceChanged) {
        this.setupWorkspaceId = workspaceId;
        this.setupIssue = undefined;
        if (this.sessionCoordinator.isOwner) {
          this.sessionCoordinator.publishRuntimeState({ setupIssue: undefined });
        }
      }
      if (!this.sessionCoordinator.isOwner) return;
      this.restoreWorkspaceSelectedIntegration();
      if (workspaceChanged) void this.requestIntegrationReconciliation();
    });

    this.userService.softphoneStateDto$.subscribe(state => {
      if (!this.currentUserId) return;
      if (!this.sessionCoordinator.isOwner) {
        if (state?.enabled && this.sessionCoordinator.currentRuntimeState.browserSessionMode === 'disabled') {
          this.sendOwnerCommand({ type: 'reconnect', payload: { forceRecreate: false } });
        }
        return;
      }
      if (!state?.enabled) {
        if (this.browserSession.mode === 'active') this.browserSession.disableLocally();
        return;
      }
      if (this.browserSession.mode === 'disabled') void this.activateOwnerBrowserSession();
    });

    this.sessionCoordinator.runtimeState$.pipe(
      filter(state => !this.sessionCoordinator.isOwner)
    ).subscribe(state => this.applyMirroredRuntimeState(state));

    this.sessionCoordinator.ownership$.subscribe(isOwner => {
      if (!isOwner) {
        this.cancelIntegrationReconciliationRetry();
        this.runtimeSnapshotConfirmed = false;
        this.runtimeSnapshotConfirmationFailed = false;
        this.browserSession.suspendLocalOwner();
        this.ownerActiveCallPayload = null;
        this.pendingTerminationObservation = undefined;
        this.scheduledTerminationObservation = undefined;
        this.bootstrappedOwnerUserId = undefined;
        void this.destroySharedSoftphoneDevice(false);
        return;
      }

      this.watchMicrophonePermission();
      if (this.currentUserId) void this.activateOwnerBrowserSession();
    });

    this.browserSession.ownershipLost$.subscribe(() => {
      this.cancelIntegrationReconciliationRetry();
      this.runtimeSnapshotConfirmed = false;
      this.runtimeSnapshotConfirmationFailed = false;
      this.ownerActiveCallPayload = null;
      this.pendingTerminationObservation = undefined;
      this.scheduledTerminationObservation = undefined;
      this.bootstrappedOwnerUserId = undefined;
      this.ownerBootstrapGeneration += 1;
      this.runtimeSnapshotQueueGeneration += 1;
      this.runtimeSnapshotQueue = Promise.resolve();
      void this.destroySharedSoftphoneDevice(false);
      this.publishSoftphoneState('inactive', this.browserSession.mode === 'disabled'
        ? 'Soft phone is disabled'
        : 'Soft phone is active in another browser');
    });

    this.browserSession.sessionRevalidated$.subscribe(() => {
      if (!this.sessionCoordinator.isOwner || !this.browserSession.isActive) return;
      void this.publishOwnerRuntimeSnapshot('heartbeat');
    });

    this.browserSession.mode$.subscribe(mode => {
      if (this.sessionCoordinator.isOwner) {
        this.sessionCoordinator.publishRuntimeState({ browserSessionMode: mode });
      }
      if (mode === 'active' && this.sessionCoordinator.isOwner && this.currentUserId) {
        void this.bootstrapOwnerRuntime();
      } else if (mode === 'standby' || mode === 'blocked') {
        this.publishSoftphoneState('inactive', mode === 'blocked'
          ? 'Switching is blocked while a softphone call is active'
          : 'Soft phone is active in another browser');
      } else if (mode === 'unverified') {
        this.publishSoftphoneState('error', 'Soft phone ownership could not be verified');
      }
    });
    this.browserSession.activeBrowser$.subscribe(activeBrowser => {
      if (this.sessionCoordinator.isOwner) {
        this.sessionCoordinator.publishRuntimeState({ activeBrowser });
      }
    });
  }

  get twilioDiagnostics() {
    const currentTabId = this.sessionCoordinator.currentTabId;
    const ownerTabId = this.sessionCoordinator.currentRuntimeState.ownerTabId;
    const selectedRuntime = this.getSelectedRuntime();
    return {
      lastTokenFetchAt: this.lastTokenFetchAt,
      tokenLast4: selectedRuntime?.tokenLast4 || this.tokenLast4,
      identity: selectedRuntime?.device?.identity || this.sharedTwilioDevice?.identity,
      integrationId: this.selectedIntegrationId(),
      hasMoreIntegrations: this.hasMoreIntegrations,
      workspaceId: this.sharedDeviceWorkspaceId,
      tabId: currentTabId,
      isOwner: this.sessionCoordinator.isOwner,
      ownerTabId,
      ownerRecovery: this.sessionCoordinator.ownerRecoveryDiagnostics,
      coachCorrelationStages: [...this.coachCorrelationDiagnostics],
      coachAdmissions: [...(this.coachAdmissionDiagnostics || [])],
    };
  }

  get browserSessionDiagnostics() {
    return this.browserSession.diagnostics;
  }

  get isOwnerTab() {
    return this.sessionCoordinator.isOwner;
  }

  get canDelegateToOwner() {
    return this.sessionCoordinator.hasLiveOwner();
  }

  stayOnOtherBrowser() {
    this.browserSession.stayOnOtherBrowser();
  }

  async switchToThisBrowser() {
    if (!this.sessionCoordinator.isOwner) {
      this.sendOwnerCommand({ type: 'switch-browser-session', payload: {} });
      return { mode: 'standby' as const };
    }
    const response = await this.browserSession.takeover();
    return response;
  }

  async verifyBrowserSessionForAction() {
    if (!this.sessionCoordinator.isOwner) {
      this.sendOwnerCommand({ type: 'verify-browser-session', payload: {} });
      return false;
    }
    try {
      return await this.browserSession.ensureActiveForAction();
    } catch (error) {
      this.exceptions.record(error, { source: 'softphone', operation: 'verify-ownership', stage: 'browser-session' });
      this.publishSoftphoneState('error', 'Soft phone ownership could not be verified');
      return false;
    }
  }

  async verifyBrowserSessionForForwardedAction() {
    return await this.browserSession.open({ promptOnConflict: false, respectStayMarker: false });
  }

  presentBrowserSessionConflict(response: SoftphoneBrowserSessionResponseDto) {
    this.browserSession.presentConflict(response);
  }

  selectIncomingTransferTabs(workspaceId?: string) {
    return this.sessionCoordinator.selectTransferTabs(workspaceId);
  }

  isCurrentTab(tabId?: string) {
    return !!tabId && tabId === this.sessionCoordinator.currentTabId;
  }

  sendOwnerCommand(command: Omit<SoftphoneOwnerCommand, 'messageId' | 'sourceTabId' | 'targetOwnerInstanceId' | 'userId' | 'createdAt' | 'targetTabId'>) {
    return this.sessionCoordinator.sendOwnerCommand(command, 'owner');
  }

  sendOwnerCommandResult(command: SoftphoneOwnerCommand, ok: boolean, payload?: SoftphoneOwnerCommandResult['payload'], error?: string) {
    this.sessionCoordinator.sendOwnerCommandResult(command, ok, payload, error);
  }

  focusIncomingTransferTab(targetTabId: string, transferId?: string) {
    this.sessionCoordinator.sendTabCommand({ type: 'focus-incoming-transfer', targetTabId, transferId });
  }

  get incomingTransferTabFocusRequested$() {
    return this.sessionCoordinator.tabCommand$;
  }

  retryOwnerRecovery() {
    this.sessionCoordinator.retryOwnerRecovery();
  }

  focusOwnerTab() {
    return this.sessionCoordinator.focusOwnerTab();
  }

  canReleaseTabOwnershipForRecovery(command: SoftphoneReleaseOwnershipOwnerCommand) {
    return this.sessionCoordinator.canReleaseOwnerForRecovery(command);
  }

  async releaseTabOwnershipForRecovery() {
    if (!this.sessionCoordinator.isOwner) return;
    await this.destroySharedSoftphoneDevice(false);
    this.browserSession.suspendLocalOwner();
    this.sessionCoordinator.releaseOwnerForRecovery();
  }

  publishIncomingTransfer(incomingTransfer?: SoftphoneIncomingTransferSummary) {
    this.sessionCoordinator.publishRuntimeState({ incomingTransfer });
    if (this.sessionCoordinator.isOwner) {
      this.noteMirroredSnapshot('incoming-transfer-change');
    }
  }

  getIncomingTransferIdentityKeys(identity: { transferId?: string; callId?: string; }) {
    const keys: string[] = [];
    const transferId = identity.transferId?.trim();
    const callId = identity.callId?.trim();
    if (transferId) keys.push(`transfer:${transferId}`);
    if (callId) keys.push(`call:${callId}`);
    return keys;
  }

  incomingTransferIdentitiesOverlap(left: readonly string[], right: readonly string[]) {
    if (!left.length || !right.length) return false;
    const leftTransfers = left.filter(key => key.startsWith('transfer:'));
    const rightTransfers = right.filter(key => key.startsWith('transfer:'));
    if (leftTransfers.length && rightTransfers.length) {
      const rightTransferKeys = new Set(rightTransfers);
      return leftTransfers.some(key => rightTransferKeys.has(key));
    }
    const rightKeys = new Set(right);
    return left.some(key => rightKeys.has(key));
  }

  completeIncomingTransfer(keys: readonly string[], now = Date.now()) {
    this.pruneCompletedIncomingTransfers(now);
    if (!keys.length) return;
    this.completedIncomingTransfers.push({
      keys: [...new Set(keys)],
      expiresAt: now + this.incomingTransferTerminalTtlMs,
    });
  }

  isIncomingTransferCompleted(keys: readonly string[], now = Date.now()) {
    this.pruneCompletedIncomingTransfers(now);
    return this.completedIncomingTransfers.some(entry => this.incomingTransferIdentitiesOverlap(entry.keys, keys));
  }

  private pruneCompletedIncomingTransfers(now: number) {
    for (let index = this.completedIncomingTransfers.length - 1; index >= 0; index -= 1) {
      if (this.completedIncomingTransfers[index].expiresAt <= now) {
        this.completedIncomingTransfers.splice(index, 1);
      }
    }
  }

  publishActiveCall(activeCall?: SoftphoneSessionActiveCallSummary) {
    this.setOwnerActiveCallPayload(activeCall?.callId && activeCall.integrationId
      ? {
        callId: activeCall.callId,
        integrationId: activeCall.integrationId,
        transferId: activeCall.transferId,
        callSid: activeCall.callSid,
        startedAt: activeCall.startedAt ? new Date(activeCall.startedAt) : new Date(),
        source: activeCall.source,
      }
      : null);
    this.sessionCoordinator.publishRuntimeState({ activeCall });
    if (!this.sessionCoordinator.isOwner) return Promise.resolve();
    const published = this.publishOwnerRuntimeSnapshot(activeCall ? 'active-call-start' : 'active-call-end');
    if (!activeCall) this.requestIntegrationReconciliation();
    return published;
  }

  clearActiveCallIfCurrent(expected: {
    callId: string;
    integrationId: string;
    transferId?: string;
    callSid?: string;
  }) {
    const current = this.ownerActiveCallPayload;
    if (!current
      || current.integrationId !== expected.integrationId
      || !this.sameTerminationLeg(current, expected)) {
      return Promise.resolve(false);
    }

    return this.publishActiveCall(undefined).then(() => true);
  }

  async publishOwnerRuntimeSnapshot(
    reason: Exclude<SoftphoneSnapshotReason, 'incoming-transfer-change'>,
    options: { activeCall?: SoftphoneOwnedActiveCallPayload; enabled?: boolean; } = {}
  ) {
    if (options.activeCall !== undefined) this.setOwnerActiveCallPayload(options.activeCall);
    return await this.publishRuntimeSnapshot(reason, options.enabled ?? true);
  }

  queueTerminationObservation(reason: SoftphoneTerminationReason) {
    const activeCall = this.ownerActiveCallPayload;
    if (!activeCall?.callId) return;
    const next = {
      callId: activeCall.callId,
      transferId: activeCall.transferId,
      callSid: activeCall.callSid,
      reason,
    };
    const pending = this.pendingTerminationObservation;
    if (pending && !this.sameTerminationLeg(pending, next)) this.pendingTerminationObservation = undefined;
    const current = this.pendingTerminationObservation;
    if (!current
      || SOFTPHONE_TERMINATION_REASON_STRENGTH[next.reason] > SOFTPHONE_TERMINATION_REASON_STRENGTH[current.reason]) {
      this.pendingTerminationObservation = next;
    }
  }

  async acquireSoftphoneDevice(consumerId: string, options: { forceRecreate?: boolean; ensureMicrophone?: boolean; integrationId?: string; } = {}) {
    this.sharedDeviceConsumers.add(consumerId);
    const context = {
      source: 'softphone', operation: 'device-acquisition', integrationId: options.integrationId,
      ...(this.activeCoachContext?.consumerId === consumerId ? this.activeCoachContext : {}),
    };
    let stage = 'tab-ownership';
    try {
      await this.sessionCoordinator.ensureOwner();
      if (!this.sessionCoordinator.isOwner) {
        this.publishSoftphoneState('loading', 'Soft phone is active in another tab');
        this.exceptions.record('Device unavailable: another tab owns the soft phone', { ...context, stage });
        return undefined;
      }
      stage = 'browser-session';
      if (!await this.browserSession.ensureActiveForAction()) {
        this.publishSoftphoneState('inactive', 'Soft phone is active in another browser');
        this.exceptions.record('Device unavailable: browser session is inactive', { ...context, stage });
        return undefined;
      }
      stage = 'device-initialization';
      await this.getOrCreateSoftphoneDevice(options);
      const device = this.getManagedDeviceForIntegration(options.integrationId) || this.sharedTwilioDevice;
      if (!device) this.exceptions.record('Device unavailable after initialization or selection', { ...context, stage: 'device-selection' });
      return device;
    } catch (error: any) {
      const initStage = error?.stage;
      this.exceptions.record(error, { ...context, stage: initStage === 'ensure-microphone-access' ? 'microphone-access' : stage });
      throw error;
    }
  }

  beginCoachIncomingSession(consumerId: string, context: Omit<CoachCorrelationContext, 'consumerId' | 'coachCallSid'> = {}) {
    this.activeCoachConsumerId = consumerId;
    this.activeCoachCallSid = undefined;
    this.activeCoachContext = { ...context, consumerId };
    this.recordCoachCorrelationStage('intent-started');
  }

  setCoachIncomingCallSid(consumerId: string, callSid?: string) {
    if (this.activeCoachConsumerId !== consumerId) return;
    this.activeCoachCallSid = callSid;
    this.activeCoachContext = { ...this.activeCoachContext, consumerId, coachCallSid: callSid };
    this.recordCoachCorrelationStage('coach-sid-received');
  }

  clearCoachIncomingSession(consumerId: string) {
    if (this.activeCoachConsumerId !== consumerId) return;
    this.recordCoachCorrelationStage('intent-cleared');
    if (this.activeCoachContext) {
      this.recentCoachContext = { context: { ...this.activeCoachContext }, expiresAt: Date.now() + 60_000 };
    }
    this.activeCoachConsumerId = undefined;
    this.activeCoachCallSid = undefined;
    this.activeCoachContext = undefined;
  }

  recordCoachCorrelationStage(stage: CoachCorrelationStage, context: CoachCorrelationContext = {}) {
    const entry = {
      ...this.activeCoachContext,
      ...context,
      stage,
      timestamp: new Date().toISOString(),
    };
    this.coachCorrelationDiagnostics.push(entry);
    if (this.coachCorrelationDiagnostics.length > 50) this.coachCorrelationDiagnostics.shift();
  }

  private coachAdmissionDiagnostics: CoachAdmissionDiagnostic[] = [];

  recordCoachAdmission(diagnostic: Omit<CoachAdmissionDiagnostic, 'timestamp'>) {
    const entry = { ...diagnostic, timestamp: new Date().toISOString() };
    this.coachAdmissionDiagnostics = [
      ...(this.coachAdmissionDiagnostics || []).filter(existing => existing.transferId !== diagnostic.transferId),
      entry,
    ].slice(-10);
  }

  isCoachIncomingCall(call: Call, integrationId?: string) {
    const now = Date.now();
    if (this.recentCoachContext && this.recentCoachContext.expiresAt <= now) this.recentCoachContext = undefined;
    const diagnosticContext = this.activeCoachContext || this.recentCoachContext?.context;
    const incomingSid = call?.parameters?.['CallSid'];
    if (diagnosticContext) {
      this.recordCoachCorrelationStage('incoming-received', { ...diagnosticContext, integrationId, coachCallSid: incomingSid });
    }
    if (!this.activeCoachConsumerId) {
      if (diagnosticContext) this.recordCoachCorrelationStage('incoming-mismatched', { ...diagnosticContext, integrationId, coachCallSid: incomingSid });
      return false;
    }

    const matched = !this.activeCoachCallSid || !incomingSid || incomingSid === this.activeCoachCallSid;
    this.recordCoachCorrelationStage(matched ? 'incoming-matched' : 'incoming-mismatched', {
      integrationId,
      coachCallSid: incomingSid,
    });

    return matched;
  }

  async releaseSoftphoneDevice(consumerId: string, destroyWhenUnused = true) {
    this.sharedDeviceConsumers.delete(consumerId);
    this.clearCoachIncomingSession(consumerId);
    if (!this.sessionCoordinator.isOwner) return;
    if (!destroyWhenUnused || this.sharedDeviceConsumers.size > 0) return;
    await this.destroySharedSoftphoneDevice();
  }

  async recoverSoftphoneDevice(consumerId: string, options: { ensureMicrophone?: boolean; forceRecreate?: boolean; integrationId?: string; } = {}) {
    this.sharedDeviceConsumers.add(consumerId);
    if (!this.sessionCoordinator.isOwner) {
      this.sendOwnerCommand({
        type: 'reconnect',
        payload: {
          integrationId: options.integrationId,
          forceRecreate: options.forceRecreate,
          ensureMicrophone: options.ensureMicrophone,
        },
      });
      this.publishSoftphoneState('loading', 'Reconnecting soft phone in owner tab...');
      return undefined;
    }
    if (!await this.browserSession.ensureActiveForAction()) {
      this.publishSoftphoneState('inactive', 'Soft phone is active in another browser');
      return undefined;
    }
    this.publishSoftphoneState('loading', 'Reconnecting soft phone...');
    if (options.integrationId) {
      const runtime = this.runtimeByIntegrationId.get(options.integrationId);
      if (runtime) {
        if (options.ensureMicrophone) await this.ensureMicrophoneAccess();
        await this.refreshManagedRuntime(options.integrationId, options.forceRecreate ?? true);
        return this.getManagedDeviceForIntegration(options.integrationId) || this.sharedTwilioDevice;
      }
    }
    await this.getOrCreateSoftphoneDevice({
      forceRecreate: true,
      ensureMicrophone: options.ensureMicrophone,
      integrationId: options.integrationId,
    });
    return this.getManagedDeviceForIntegration(options.integrationId) || this.sharedTwilioDevice;
  }

  setSelectedIntegrationId(integrationId?: string) {
    this.applySelectedIntegrationId(integrationId, { persist: true });
  }

  private applySelectedIntegrationId(integrationId?: string, options: { persist?: boolean; } = {}) {
    if (options.persist) {
      this.storeWorkspaceSelectedIntegration(integrationId);
    }

    const currentIntegrationId = this.selectedIntegrationId();
    if (currentIntegrationId !== integrationId) {
      this.selectedIntegrationId.set(integrationId);
    }

    this.syncSelectedDeviceReference();
    if (this.sessionCoordinator.isOwner) {
      this.publishDerivedRuntimeState();
    }
  }

  private async bootstrapOwnerRuntime(force = false) {
    if (!this.sessionCoordinator.isOwner || !this.currentUserId) return;
    if (!this.browserSession.isActive) return;
    const generation = this.ownerBootstrapGeneration;
    const userId = this.currentUserId;
    const sessionId = this.browserSession.sessionId;
    if (this.ownerBootstrapInFlightGeneration === generation) return;
    if (!force && this.bootstrappedOwnerUserId === this.currentUserId) return;

    this.ownerBootstrapInFlightGeneration = generation;
    try {
      await this.getOrCreateSoftphoneDevice({ forceRecreate: force, ensureMicrophone: false });
      if (generation === this.ownerBootstrapGeneration
        && userId === this.currentUserId
        && this.browserSession.ownsSession(sessionId)) {
        this.bootstrappedOwnerUserId = userId;
      }
    } catch {
      // getOrCreateSoftphoneDevice publishes the current initialization error; stale generations are ignored.
    } finally {
      if (this.ownerBootstrapInFlightGeneration === generation) {
        this.ownerBootstrapInFlightGeneration = undefined;
      }
    }
  }

  private async activateOwnerBrowserSession() {
    if (!this.sessionCoordinator.isOwner || !this.currentUserId) return;
    try {
      await this.browserSession.open({ promptOnConflict: true });
      if (!this.sessionCoordinator.isOwner || !this.currentUserId) return;
      if (this.browserSession.isActive) await this.bootstrapOwnerRuntime();
      else if (this.browserSession.mode === 'disabled') this.publishDisabledRuntimeState();
    } catch (error) {
      if (this.sessionCoordinator.isOwner && this.browserSession.isActive) return;
      this.exceptions.record(error, { source: 'softphone', operation: 'activate-owner', stage: 'browser-session' });
      await this.destroySharedSoftphoneDevice(false);
      this.publishSoftphoneState('error', 'Soft phone ownership could not be verified');
    }
  }

  private async getOrCreateSoftphoneDevice(options: { forceRecreate?: boolean; ensureMicrophone?: boolean; integrationId?: string; } = {}) {
    const { forceRecreate = false, ensureMicrophone = false, integrationId: requestedIntegrationId } = options;
    if (!this.sessionCoordinator.isOwner) throw new Error('Soft phone runtime is owned by another tab.');
    if (!this.browserSession.isActive) throw new Error('Soft phone is active in another browser.');
    const initGeneration = this.ownerBootstrapGeneration;
    const initUserId = this.currentUserId;
    const initSessionId = this.browserSession.sessionId;
    this.publishSoftphoneState('loading', 'Connecting soft phone...');
    this.runtimeLifecycleStatus = forceRecreate ? 'recovering' : 'starting';
    this.runtimeBootstrapStartedAt = Date.now();
    const workspaceId = this.workspaceService.workspaceId;
    const selectedRuntime = this.getSelectedRuntime();
    if (selectedRuntime?.device && !forceRecreate) {
      if (ensureMicrophone) await this.ensureMicrophoneAccess();
      this.syncSelectedDeviceReference();
      this.publishDerivedRuntimeState();
      return this.sharedTwilioDevice;
    }

    if (this.sharedDeviceInitPromise
      && this.sharedDeviceInitPromiseGeneration === initGeneration
      && !forceRecreate) return this.sharedDeviceInitPromise;

    this.sharedDeviceInitPromiseGeneration = initGeneration;
    this.sharedDeviceInitPromise = (async () => {
      let stage = 'start';
      try {
        stage = 'ensure-microphone-access';
        if (ensureMicrophone) await this.ensureMicrophoneAccess();

        stage = 'fetch-softphone-state';
        const softphoneState = await this.userService.getSoftphoneState();
        if (initGeneration !== this.ownerBootstrapGeneration
          || initUserId !== this.currentUserId
          || !this.browserSession.ownsSession(initSessionId)) {
          throw new Error('Soft phone browser session was lost.');
        }
        if (!softphoneState.enabled) {
          await this.destroySharedSoftphoneDevice(false);
          this.hasMoreIntegrations = false;
          this.applySelectedIntegrationId(undefined);
          this.runtimeLifecycleStatus = 'active';
          this.publishDisabledRuntimeState();
          await this.publishOwnerRuntimeSnapshot('bootstrap', { activeCall: null, enabled: false });
          return undefined;
        }
        if (workspaceId === this.workspaceService.workspaceId) this.setupIssue = softphoneState.setupIssue;
        const preferredIntegrationId = this.selectPreferredSoftphoneIntegrationId({
          requestedIntegrationId,
          currentSelectedIntegrationId: this.selectedIntegrationId(),
          storedWorkspaceIntegrationId: this.getStoredWorkspaceSelectedIntegration(),
          resolvedIntegrations: softphoneState.resolvedIntegrations,
        });
        if (preferredIntegrationId) {
          this.setSelectedIntegrationId(preferredIntegrationId);
        } else {
          this.clearStoredWorkspaceSelectedIntegration();
        }
        this.hasMoreIntegrations = softphoneState.resolvedIntegrations.length > 1;

        stage = 'load-twilio-sdk';
        await this.loadTwilioSdk();
        if (initGeneration !== this.ownerBootstrapGeneration
          || initUserId !== this.currentUserId
          || !this.browserSession.ownsSession(initSessionId)) {
          throw new Error('Soft phone browser session was lost.');
        }

        stage = 'sync-resolved-integrations';
        const requiresAuthoritativeReconciliation = await this.syncResolvedIntegrations(
          softphoneState.resolvedIntegrations,
          forceRecreate,
          initSessionId,
        );
        if (!this.sessionCoordinator.isOwner || initGeneration !== this.ownerBootstrapGeneration || !this.browserSession.ownsSession(initSessionId)) {
          throw new Error('Soft phone browser session was lost.');
        }
        if (requiresAuthoritativeReconciliation) this.requestIntegrationReconciliation();

        stage = 'activate-runtime';
        this.syncSelectedDeviceReference();
        this.sharedDeviceWorkspaceId = workspaceId;
        this.startRuntimeSnapshotHeartbeat();
        this.publishDerivedRuntimeState();
        void this.publishOwnerRuntimeSnapshot('bootstrap');

        return this.sharedTwilioDevice as Device | undefined;
      } catch (error: any) {
        throw {
          name: 'SoftphoneInitError',
          message: error?.message || `Softphone initialization failed at stage "${stage}".`,
          stage,
          ensureMicrophone,
          forceRecreate,
          workspaceId,
          cause: error,
        };
      }
    })().catch(error => {
      const isCurrentInitialization = initGeneration === this.ownerBootstrapGeneration
        && initUserId === this.currentUserId
        && this.browserSession.ownsSession(initSessionId);
      if (!isCurrentInitialization) throw error;
      this.exceptions.record(error, { source: 'softphone', operation: 'device-initialization', stage: error?.stage === 'ensure-microphone-access' ? 'microphone-access' : error?.stage, integrationId: requestedIntegrationId });
      const sessionActive = this.browserSession.isActive;
      this.runtimeLifecycleStatus = sessionActive ? 'error' : 'releasing';
      this.publishSoftphoneState(
        sessionActive ? 'error' : 'inactive',
        sessionActive
          ? this.getSoftphoneErrorMessage(error?.cause || error, 'Failed to initialize the soft phone.')
          : 'Soft phone is active in another browser'
      );
      if (this.sessionCoordinator.isOwner) void this.publishOwnerRuntimeSnapshot('connection-change');
      throw error;
    }).finally(() => {
      if (initGeneration === this.ownerBootstrapGeneration && this.runtimeLifecycleStatus === 'starting') {
        this.runtimeLifecycleStatus = 'active';
      }
      if (this.sharedDeviceInitPromiseGeneration === initGeneration) {
        this.sharedDeviceInitPromise = undefined;
        this.sharedDeviceInitPromiseGeneration = undefined;
      }
    });

    return this.sharedDeviceInitPromise;
  }

  private scheduleTokenRefresh(runtime: SoftphoneManagedRuntime, expiresAt?: string) {
    this.stopTokenRefreshTimer(runtime);
    if (!expiresAt) return;

    const expiresAtMs = Date.parse(expiresAt);
    if (!Number.isFinite(expiresAtMs)) return;

    const refreshAt = expiresAtMs - (5 * 60 * 1000);
    const serverNow = Date.now() + this.serverClockOffsetMs;
    const delay = Math.max(15 * 1000, refreshAt - serverNow);

    runtime.tokenRefreshTimeout = setTimeout(() => {
      void this.refreshManagedRuntime(runtime.integrationId);
    }, delay);
  }

  private stopTokenRefreshTimer(runtime: SoftphoneManagedRuntime) {
    if (runtime.tokenRefreshTimeout) clearTimeout(runtime.tokenRefreshTimeout);
    runtime.tokenRefreshTimeout = undefined;
  }

  private updateServerClockOffset(serverTime?: string) {
    if (!serverTime) return;
    const serverTimeMs = Date.parse(serverTime);
    if (!Number.isFinite(serverTimeMs)) return;
    this.serverClockOffsetMs = serverTimeMs - Date.now();
  }

  private async loadTwilioSdk() {
    if (window.Twilio?.Device) return;

    if (!SoftphoneService.twilioSdkPromise) {
      SoftphoneService.twilioSdkPromise = import('@twilio/voice-sdk')
        .then(module => {
          const Device = module?.Device;
          if (!Device) throw new Error('Twilio Voice SDK failed to load.');
          window.Twilio = { ...(window.Twilio || {}), Device };
        })
        .catch(error => {
          SoftphoneService.twilioSdkPromise = undefined;
          throw error;
        });
    }

    await SoftphoneService.twilioSdkPromise;
  }

  microphonePermission?: string;
  private microphoneBlocked = false;
  private microphonePermissionWatch?: Promise<void>;

  get isMicrophoneBlocked() {
    return this.microphoneBlocked;
  }

  /** Reads the browser permission without prompting; only an explicit denial blocks transfers. */
  private watchMicrophonePermission() {
    if (this.microphonePermissionWatch || typeof navigator === 'undefined' || !navigator.permissions?.query) return;
    this.microphonePermissionWatch = navigator.permissions.query({ name: 'microphone' as PermissionName })
      .then(status => {
        status.addEventListener('change', () => this.applyMicrophonePermission(status.state));
        this.applyMicrophonePermission(status.state);
      })
      .catch(() => undefined);
  }

  private applyMicrophonePermission(permission: PermissionState) {
    this.microphonePermission = permission;
    this.setMicrophoneBlocked(permission === 'denied');
  }

  noteMicrophoneFailure(error: any) {
    if (getSoftphoneMicrophoneFailureKind(error) !== 'microphone-blocked') return;
    // A dismissed prompt is not a denial; only denied (or an OS/policy block behind granted) blocks.
    if (this.microphonePermission === 'prompt') return;
    this.microphonePermission = 'denied';
    this.setMicrophoneBlocked(true);
  }

  async recheckMicrophoneAccess(): Promise<{ ok: true } | { ok: false; error: unknown }> {
    try {
      await this.ensureMicrophoneAccess();
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  }

  private setMicrophoneBlocked(blocked: boolean) {
    if (!!this.microphoneBlocked === blocked) return;
    this.microphoneBlocked = blocked;
    if (!this.sessionCoordinator.isOwner) return;
    this.sessionCoordinator.publishRuntimeState({ microphoneBlocked: blocked });
    if (!this.runtimeByIntegrationId.size) return;
    this.publishDerivedRuntimeState();
    void this.publishOwnerRuntimeSnapshot('connection-change');
  }

  private async ensureMicrophoneAccess() {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(error => {
      this.noteMicrophoneFailure(error);
      this.exceptions.record(error, { source: 'softphone', operation: 'device-acquisition', stage: 'microphone-access' });
      throw error;
    });
    this.microphonePermission = 'granted';
    this.setMicrophoneBlocked(false);
    try { stream.getTracks().forEach(track => track.stop()); }
    catch (error) {
      this.exceptions.record(error, { source: 'softphone', operation: 'microphone-track-cleanup' });
      throw error;
    }
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        void Notification.requestPermission().catch(error => {
          this.exceptions.record(error, { source: 'softphone', operation: 'notification-permission' });
        });
      } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'notification-permission' }); }
    }
  }

  private async destroySharedSoftphoneDevice(publishState = true) {
    this.stopRuntimeSnapshotHeartbeat();
    for (const runtime of this.runtimeByIntegrationId.values()) {
      this.stopTokenRefreshTimer(runtime);
      if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
      runtime.retryTimeout = undefined;
      if (runtime.device) this.unbindManagedRuntimeEvents(runtime, runtime.device);
      try { runtime.device?.disconnectAll?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'disconnect-cleanup', integrationId: runtime.integrationId }); }
      try { runtime.device?.destroy?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'device-destroy-cleanup', integrationId: runtime.integrationId }); }
    }
    this.runtimeByIntegrationId.clear();
    this.setSharedTwilioDevice(undefined);
    this.sharedDeviceWorkspaceId = undefined;
    this.ownerActiveCallPayload = null;
    this.pendingTerminationObservation = undefined;
    this.scheduledTerminationObservation = undefined;
    this.runtimeLifecycleStatus = 'releasing';
    if (publishState) {
      this.publishSoftphoneState('inactive', 'Soft phone is off');
      if (this.sessionCoordinator.isOwner) void this.publishOwnerRuntimeSnapshot('shutdown', { activeCall: null });
    }
  }

  private setSharedTwilioDevice(device?: Device) {
    this.sharedTwilioDevice = device;
    this.sharedTwilioDeviceSubject.next(device);
  }

  publishSoftphoneState(state: SoftphoneState, detail?: string) {
    if (state === 'ready' && this.microphoneBlocked) {
      state = 'error';
      detail = SOFTPHONE_MICROPHONE_BLOCKED_DETAIL;
    }
    this.softphoneStateSubject.next(state);
    this.softphoneStateDetailSubject.next(detail);
    if (!this.sessionCoordinator.isOwner) return;
    this.sessionCoordinator.publishRuntimeState({
      state,
      detail,
      microphoneBlocked: this.microphoneBlocked,
      selectedIntegrationId: this.selectedIntegrationId(),
      hasMoreIntegrations: this.hasMoreIntegrations,
    });
  }

  private publishDisabledRuntimeState(detail = 'Soft phone is disabled') {
    this.setupIssue = undefined;
    this.softphoneStateSubject.next('inactive');
    this.softphoneStateDetailSubject.next(detail);
    if (!this.sessionCoordinator.isOwner) return;
    this.sessionCoordinator.publishRuntimeState({
      state: 'inactive',
      detail,
      setupIssue: undefined,
      ownerLifecycleStatus: this.runtimeLifecycleStatus,
      bootstrapStartedAt: this.runtimeBootstrapStartedAt,
      hasPartialConnectionWarning: false,
      selectedIntegrationId: undefined,
      hasMoreIntegrations: false,
      lastSnapshotPublishedAt: this.runtimeLastSnapshotPublishedAt,
      lastSnapshotReason: this.runtimeLastSnapshotReason,
      connections: [],
    });
  }

  requestSoftphoneCallLead(intent: SoftphoneCallLeadIntent) {
    this.softphoneCallLeadIntentSubject.next(intent);
  }

  requestSoftphonePanelOpen(source: 'app-shell' | 'call-lead' | 'incoming-transfer') {
    this.softphonePanelOpenRequestSubject.next({ source });
  }

  resolveSoftphoneFromNumber(payload: {
    integrationId?: string;
    previousCallId?: string;
    preferredFromNumber?: string;
    leadPhone: string;
    leadState?: string;
  }, workspaceId?: string) {
    return firstValueFrom(this.http.post<SoftphoneFromNumberResolution>(`${environment.app.serviceUrl}/twilio/softphone/resolve-from-number`, {
      ...payload,
      integrationId: payload.integrationId || this.selectedIntegrationId(),
    }, workspaceId ? { headers: { 'x-voxr-workspace': workspaceId } } : undefined));
  }

  getSoftphoneOriginPreference(integrationId: string) {
    return firstValueFrom(this.http.get<SoftphoneOriginPreferenceDto | null>(
      `${environment.app.serviceUrl}/twilio/softphone/origin-preference/${encodeURIComponent(integrationId)}`
    ));
  }

  getSoftphoneOriginNumberOptions(integrationId: string, q?: string) {
    return firstValueFrom(this.http.get<SoftphoneOriginNumberOptionDto[]>(
      `${environment.app.serviceUrl}/twilio/softphone/origin-number-options/${encodeURIComponent(integrationId)}`,
      { params: q?.trim() ? { q: q.trim() } : {} }
    ));
  }

  async saveSoftphoneOriginPreference(payload: {
    integrationId: string;
    mode: SoftphoneOriginNumberMode;
    phoneNumberId?: string;
  }) {
    const result = await firstValueFrom(this.http.post<SoftphoneOriginPreferenceDto>(
      `${environment.app.serviceUrl}/twilio/softphone/origin-preference`,
      payload
    ));
    await this.userService.getSoftphoneState();
    return result;
  }

  prepareSoftphoneCall(payload: {
    targetNumber: string;
    integrationId?: string;
    preferredFromNumber?: string;
    campaignId?: string;
    previousCallId?: string;
    source?: SoftphoneCallLeadIntent['source'] | 'manual';
  }, workspaceId?: string) {
    if (!this.browserSession.isActive) {
      void this.browserSession.ensureActiveForAction().catch(() => undefined);
      return Promise.reject(new Error('Switch the soft phone to this browser before placing a call.'));
    }
    const headers = workspaceId
      ? this.browserSession.sessionHeaders().set('x-voxr-workspace', workspaceId)
      : this.browserSession.sessionHeaders();
    return firstValueFrom(this.http.post<SoftphonePrepareCallResponse>(`${environment.app.serviceUrl}/twilio/softphone/prepare-call`, {
      ...payload,
      integrationId: payload.integrationId || this.selectedIntegrationId(),
    }, { headers })).then(result => { this.receiveQualityTelemetry(result.softphoneQualityTelemetry); return result; }).catch(error => {
      this.browserSession.handleSessionConflict(error);
      throw error;
    });
  }

  releaseSoftphoneCallReservation(callId: string) {
    return firstValueFrom(this.http.post<{ released: boolean }>(
      `${environment.app.serviceUrl}/twilio/softphone/release-call-reservation`,
      { callId },
      { headers: this.browserSession.sessionHeaders() }
    )).catch(error => {
      this.browserSession.handleSessionConflict(error);
      throw error;
    });
  }

  threeWayCall(payload: { callSid: string; targetNumber: string; callId: string; activityId?: string; }) {
    return this.handleConferenceRequest(firstValueFrom(this.http.post<{ success: boolean; threeWayCallSid: string; conferenceName: string; conferenceSid?: string; callId?: string }>(
      `${environment.app.serviceUrl}/twilio/three-way`,
      payload,
      { headers: this.browserSession.sessionHeaders() }
    )));
  }

  getParticipants(callId: string, activityId?: string) {
    return this.handleConferenceRequest(firstValueFrom(this.http.get<SoftphoneParticipantSnapshotDto>(
      `${environment.app.serviceUrl}/twilio/calls/${encodeURIComponent(callId)}/participants`,
      {
        context: new HttpContext().set(RECORD_HTTP_ERROR_WITHOUT_TOAST, true),
        headers: this.browserSession.sessionHeaders(),
        params: activityId ? { activityId } : undefined,
      }
    )));
  }

  participantAction(payload: {
    callId: string;
    participantCallSid: string;
    action: 'mute' | 'unmute' | 'hold' | 'unhold';
    activityId?: string;
  }) {
    return this.handleConferenceRequest(firstValueFrom(this.http.post<SoftphoneParticipantMutationResponseDto>(
      `${environment.app.serviceUrl}/twilio/calls/${encodeURIComponent(payload.callId)}/participants/${encodeURIComponent(payload.participantCallSid)}/actions`,
      { action: payload.action, activityId: payload.activityId },
      { headers: this.browserSession.sessionHeaders() }
    )));
  }

  endParticipant(payload: { callId: string; participantCallSid: string; activityId?: string; }) {
    return this.handleConferenceRequest(firstValueFrom(this.http.post<SoftphoneParticipantMutationResponseDto>(
      `${environment.app.serviceUrl}/twilio/calls/${encodeURIComponent(payload.callId)}/participants/${encodeURIComponent(payload.participantCallSid)}/end`,
      { activityId: payload.activityId },
      { headers: this.browserSession.sessionHeaders() }
    )));
  }

  endConference(callId: string, activityId?: string) {
    return this.handleConferenceRequest(firstValueFrom(this.http.post<SoftphoneParticipantMutationResponseDto>(
      `${environment.app.serviceUrl}/twilio/calls/${encodeURIComponent(callId)}/conference/end-all`,
      { activityId },
      { headers: this.browserSession.sessionHeaders() }
    )));
  }

  private handleConferenceRequest<T>(request: Promise<T>) {
    return request.catch(error => {
      this.browserSession.handleSessionConflict(error);
      throw error;
    });
  }

  private maskTokenLast4(token?: string) {
    try {
      if (!token || typeof token !== 'string') return undefined;
      const last4 = token.slice(-4);
      return `****${last4}`;
    } catch {
      return undefined;
    }
  }

  private applyMirroredRuntimeState(state: SoftphoneMirroredRuntimeState) {
    this.softphoneStateSubject.next(state.state);
    this.softphoneStateDetailSubject.next(state.state === 'ready'
      ? 'The calling connection is running in another VOXR tab.'
      : state.detail);
    this.hasMoreIntegrations = !!state.hasMoreIntegrations;
  }

  private selectPreferredSoftphoneIntegrationId(input: {
    requestedIntegrationId?: string;
    currentSelectedIntegrationId?: string;
    storedWorkspaceIntegrationId?: string;
    resolvedIntegrations: SoftphoneResolvedIntegrationSummary[];
  }) {
    const availableIds = new Set(input.resolvedIntegrations.map(connection => connection.integrationId));
    for (const candidate of [input.requestedIntegrationId, input.currentSelectedIntegrationId, input.storedWorkspaceIntegrationId]) {
      if (candidate && availableIds.has(candidate)) return candidate;
    }
    return input.resolvedIntegrations.find(connection => connection.usage !== 'draining' && connection.connectionStatus === 'connected')?.integrationId
      || input.resolvedIntegrations.find(connection => connection.usage === 'draining' && connection.connectionStatus === 'connected')?.integrationId
      || input.resolvedIntegrations[0]?.integrationId;
  }

  private mapRuntimeStatusToSnapshotStatus(status: SoftphoneMirroredConnectionStatus): SoftphoneConnectionStatus {
    switch (status) {
      case 'connected':
        return 'connected';
      case 'connecting':
      case 'retrying':
        return 'connecting';
      case 'error':
        return 'error';
      default:
        return 'disconnected';
    }
  }

  private getSoftphoneRetryDelayMs(retryAttempt: number) {
    const retryDelaysMs = [0, 5000, 15000, 30000, 60000];
    return retryDelaysMs[Math.min(Math.max(retryAttempt, 0), retryDelaysMs.length - 1)];
  }

  private getSelectedRuntime() {
    const selectedIntegrationId = this.selectedIntegrationId();
    const runtime = selectedIntegrationId ? this.runtimeByIntegrationId.get(selectedIntegrationId) : undefined;
    return runtime?.authorized === false ? undefined : runtime;
  }

  private getManagedDeviceForIntegration(integrationId?: string) {
    return integrationId ? this.runtimeByIntegrationId.get(integrationId)?.device : undefined;
  }

  private syncSelectedDeviceReference() {
    const selectedRuntime = this.getSelectedRuntime();
    this.setSharedTwilioDevice(selectedRuntime?.device);
  }

  private restoreWorkspaceSelectedIntegration() {
    const storedIntegrationId = this.getStoredWorkspaceSelectedIntegration();
    if (!storedIntegrationId || storedIntegrationId === this.selectedIntegrationId()) return;

    if (this.runtimeByIntegrationId.size > 0 && !this.runtimeByIntegrationId.has(storedIntegrationId)) {
      this.clearStoredWorkspaceSelectedIntegration();
      return;
    }

    this.applySelectedIntegrationId(storedIntegrationId);
  }

  private selectedIntegrationStorageKey() {
    return this.currentUserId ? `${this.selectedIntegrationStoragePrefix}.${this.currentUserId}.v1` : undefined;
  }

  private getStoredWorkspaceSelectedIntegration() {
    const workspaceId = this.workspaceService.workspaceId;
    const storageKey = this.selectedIntegrationStorageKey();
    if (!workspaceId || !storageKey) return undefined;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return undefined;
      const map = JSON.parse(raw) as Record<string, string | undefined>;
      const integrationId = map?.[workspaceId];
      return typeof integrationId === 'string' && integrationId.trim() ? integrationId : undefined;
    } catch (error) {
      this.exceptions.record(error, { source: 'softphone', operation: 'read-selected-integration', stage: 'device-selection' });
      return undefined;
    }
  }

  private storeWorkspaceSelectedIntegration(integrationId?: string) {
    const workspaceId = this.workspaceService.workspaceId;
    const storageKey = this.selectedIntegrationStorageKey();
    if (!workspaceId || !storageKey) return;

    try {
      const raw = localStorage.getItem(storageKey);
      const map = raw ? JSON.parse(raw) as Record<string, string | undefined> : {};
      if (integrationId?.trim()) map[workspaceId] = integrationId;
      else delete map[workspaceId];
      localStorage.setItem(storageKey, JSON.stringify(map));
    } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'store-selected-integration', stage: 'device-selection' }); }
  }

  private clearStoredWorkspaceSelectedIntegration() {
    this.storeWorkspaceSelectedIntegration(undefined);
  }

  private async syncResolvedIntegrations(
    resolvedIntegrations: Array<{ integrationId: string; accountSid?: string; title?: string; usage: 'current' | 'draining' }>,
    forceRecreate = false,
    expectedSessionId?: string
  ) {
    if (expectedSessionId && !this.browserSession.ownsSession(expectedSessionId)) {
      throw new Error('Soft phone browser session was lost.');
    }
    const generation = this.ownerBootstrapGeneration;
    const sessionId = expectedSessionId || this.browserSession.sessionId;
    const nextIntegrationIds = new Set(resolvedIntegrations.map(connection => connection.integrationId));
    for (const [integrationId, runtime] of this.runtimeByIntegrationId.entries()) {
      if (nextIntegrationIds.has(integrationId)) continue;
      runtime.authorized = false;
      this.stopTokenRefreshTimer(runtime);
      if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
      runtime.retryTimeout = undefined;
      if (this.managedRuntimeHasCall(runtime)) continue;
      await this.destroyManagedRuntime(runtime);
      this.runtimeByIntegrationId.delete(integrationId);
    }

    const runtimesToConnect: SoftphoneManagedRuntime[] = [];
    for (const resolved of resolvedIntegrations) {
      const existing = this.runtimeByIntegrationId.get(resolved.integrationId) || {
        integrationId: resolved.integrationId,
        usage: resolved.usage,
        authorized: true,
        status: 'disconnected',
        retryAttempt: 0,
      } as SoftphoneManagedRuntime;
      existing.accountSid = resolved.accountSid;
      existing.title = resolved.title;
      existing.usage = resolved.usage;
      existing.authorized = true;
      this.runtimeByIntegrationId.set(resolved.integrationId, existing);
      runtimesToConnect.push(existing);
    }

    const connectResults = await Promise.allSettled(
      runtimesToConnect.map(runtime => this.ensureManagedRuntimeConnected(runtime, forceRecreate, expectedSessionId, !forceRecreate))
    );

    if (!this.sessionCoordinator.isOwner || generation !== this.ownerBootstrapGeneration || !this.browserSession.ownsSession(sessionId)) {
      throw new Error('Soft phone browser session was lost.');
    }
    const connectionFailures: unknown[] = [];
    let authoritativeFailure = false;
    for (let index = 0; index < connectResults.length; index += 1) {
      if (!this.sessionCoordinator.isOwner || generation !== this.ownerBootstrapGeneration || !this.browserSession.ownsSession(sessionId)) {
        throw new Error('Soft phone browser session was lost.');
      }
      const result = connectResults[index];
      if (result.status === 'fulfilled') continue;

      const failedRuntime = runtimesToConnect[index];
      if (!failedRuntime || this.runtimeByIntegrationId.get(failedRuntime.integrationId) !== failedRuntime) continue;
      connectionFailures.push(result.reason);
      if (this.isTerminalSoftphoneAuthFailure(result.reason)) {
        authoritativeFailure = true;
        await this.retireRejectedRuntime(failedRuntime);
      } else if (this.sessionCoordinator.isOwner && this.browserSession.isActive) {
        this.scheduleRuntimeRetry(failedRuntime);
      }
    }

    if (connectionFailures.length > 0 && !Array.from(this.runtimeByIntegrationId.values()).some(runtime => runtime.status === 'connected')) {
      throw connectionFailures[0];
    }
    return authoritativeFailure;
  }

  private async ensureManagedRuntimeConnected(runtime: SoftphoneManagedRuntime, forceRecreate = false, expectedSessionId?: string, allowTokenReuse = false): Promise<false | void> {
    const DeviceCtor = window.Twilio?.Device;
    if (!DeviceCtor) throw new Error('Twilio Voice SDK failed to load.');
    const deviceAtStart = runtime.device;
    let operationDevice = deviceAtStart;
    const sessionId = expectedSessionId || this.browserSession.sessionId;
    const generation = this.ownerBootstrapGeneration;
    const isCurrent = () => this.sessionCoordinator.isOwner
      && generation === this.ownerBootstrapGeneration
      && this.browserSession.ownsSession(sessionId)
      && this.runtimeByIntegrationId.get(runtime.integrationId) === runtime;
    if (!isCurrent()) throw new Error('Soft phone browser session was lost.');

    const shouldShowConnecting = runtime.retryAttempt === 0
      && (runtime.status === 'disconnected' || runtime.status === 'connecting');
    if (shouldShowConnecting) {
      runtime.status = 'connecting';
      runtime.error = undefined;
      this.publishDerivedRuntimeState();
    }

    try {
      if (!sessionId) throw new Error('Soft phone browser session is not active.');
      if (!isCurrent()) throw new Error('Soft phone browser session was lost.');
      const { token, expiresAt, serverTime, contractVersion, softphoneQualityTelemetry } = await this.userService.getSoftphoneToken({ integrationId: runtime.integrationId }, sessionId);
      if (!isCurrent()) throw new Error('Soft phone browser session was lost.');
      if (runtime.device !== deviceAtStart) return false;
      this.updateServerClockOffset(serverTime);
      this.observeServerContractVersion(contractVersion);
      this.lastTokenFetchAt = new Date().toISOString();
      this.receiveQualityTelemetry(softphoneQualityTelemetry);
      const recreateExistingDevice = forceRecreate
        && runtime.device === deviceAtStart
        && !this.managedRuntimeHasCall(runtime);

      if (runtime.device && !recreateExistingDevice) {
        const device = runtime.device;
        const appliedExpiry = runtime.expiresAt ? Date.parse(runtime.expiresAt) : NaN;
        const reuseToken = allowTokenReuse && !forceRecreate
          && device.state === 'registered' && runtime.status === 'connected'
          && Number.isFinite(appliedExpiry)
          && appliedExpiry > Date.now() + this.serverClockOffsetMs + 5 * 60_000;
        if (!reuseToken) {
          await device.updateToken(token);
          if (!isCurrent()) throw new Error('Soft phone browser session was lost.');
          if (runtime.device !== device) return false;
          runtime.tokenLast4 = this.maskTokenLast4(token);
          runtime.expiresAt = expiresAt;
          if (this.selectedIntegrationId() === runtime.integrationId) this.tokenLast4 = runtime.tokenLast4;
        }
        if (!isCurrent()) throw new Error('Soft phone browser session was lost.');
        const deviceState = (runtime.device as any).state;
        if (deviceState === 'unregistered') {
          await runtime.device.register?.();
          if (!isCurrent()) throw new Error('Soft phone browser session was lost.');
          if (runtime.device !== device) return false;
        } else if (deviceState === 'registered') {
          runtime.status = 'connected';
          runtime.error = undefined;
          runtime.lastSeenAt = Date.now();
          runtime.retryAttempt = 0;
          if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
          runtime.retryTimeout = undefined;
          runtime.retryAt = undefined;
          this.runtimeLifecycleStatus = 'active';
          this.syncSelectedDeviceReference();
          this.publishDerivedRuntimeState();
        }
        if (!isCurrent()) throw new Error('Soft phone browser session was lost.');
        if (!reuseToken) this.scheduleTokenRefresh(runtime, expiresAt);
        return;
      }

      if (runtime.device) {
        this.unbindManagedRuntimeEvents(runtime, runtime.device);
        try { runtime.device.disconnectAll?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'disconnect-cleanup', integrationId: runtime.integrationId }); }
        try { runtime.device.destroy?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'device-destroy-cleanup', integrationId: runtime.integrationId }); }
      }

      const device = new DeviceCtor(token, {
        logLevel: 'error',
        codecPreferences: ['opus', 'pcmu'] as Call.Codec[],
        maxCallSignalingTimeoutMs: this.callSignalingReconnectMs,
      });
      runtime.device = device;
      operationDevice = device;
      runtime.tokenLast4 = this.maskTokenLast4(token);
      runtime.expiresAt = expiresAt;
      if (this.selectedIntegrationId() === runtime.integrationId) this.tokenLast4 = runtime.tokenLast4;
      this.bindManagedRuntimeEvents(runtime, device);
      await device.register();
      if (runtime.device !== device) return false;
      if (!isCurrent()) {
        this.unbindManagedRuntimeEvents(runtime, device);
        try { device.destroy?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'device-destroy-cleanup', integrationId: runtime.integrationId }); }
        runtime.device = undefined;
        throw new Error('Soft phone browser session was lost.');
      }
      this.scheduleTokenRefresh(runtime, expiresAt);
    } catch (error: any) {
      if (!isCurrent()) throw error;
      if (runtime.device !== operationDevice) return false;
      this.exceptions.record(error, { source: 'softphone', operation: 'connect-runtime', stage: 'device-initialization', integrationId: runtime.integrationId });
      this.browserSession.handleSessionConflict(error);
      if (!isCurrent()) throw error;
      runtime.status = 'error';
      runtime.error = this.getSoftphoneErrorMessage(error, 'Failed to connect runtime');
      runtime.lastSeenAt = Date.now();
      throw error;
    }
  }

  private observeServerContractVersion(contractVersion?: number) {
    if (typeof contractVersion !== 'number') return;
    const observedVersion = this.serverContractVersion();
    if (observedVersion !== undefined && observedVersion !== SOFTPHONE_CONTRACT_VERSION) return;
    this.serverContractVersion.set(contractVersion);
  }

  private bindManagedRuntimeEvents(runtime: SoftphoneManagedRuntime, device: Device) {
    if (runtime.handlers?.device === device) return;
    if (runtime.handlers) this.unbindManagedRuntimeEvents(runtime, runtime.handlers.device);

    const handlers: SoftphoneManagedRuntimeHandlers = {
      device,
      registered: () => {
      if (runtime.device !== device) return;
      runtime.status = 'connected';
      runtime.deviceSid = (device as any)?.sid || runtime.deviceSid;
      runtime.error = undefined;
      runtime.lastSeenAt = Date.now();
      runtime.retryAttempt = 0;
      if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
      runtime.retryTimeout = undefined;
      runtime.retryAt = undefined;
      this.runtimeLifecycleStatus = 'active';
      if (!this.selectedIntegrationId()) this.setSelectedIntegrationId(runtime.integrationId);
      this.syncSelectedDeviceReference();
      this.publishDerivedRuntimeState();
      void this.publishOwnerRuntimeSnapshot('connection-change');
      this.deviceEventSubject.next({ kind: 'registered', integrationId: runtime.integrationId, device });
      },

      unregistered: () => {
      if (runtime.device !== device) return;
      runtime.status = 'disconnected';
      runtime.lastSeenAt = Date.now();
      this.deviceEventSubject.next({ kind: 'unregistered', integrationId: runtime.integrationId, device });
      if (this.sessionCoordinator.isOwner && this.browserSession.isActive) this.scheduleRuntimeRetry(runtime);
      else this.publishDerivedRuntimeState();
      void this.publishOwnerRuntimeSnapshot('connection-change');
      },

      incoming: (call: Call) => {
      if (runtime.device !== device) return;
      if (runtime.authorized === false) {
        try { call.reject(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'reject-incoming', integrationId: runtime.integrationId }); }
        return;
      }
      runtime.lastSeenAt = Date.now();
      if (this.ownerActiveCallPayload) {
        try { call.reject(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'reject-incoming', integrationId: runtime.integrationId }); }
        return;
      }
      if (this.isCoachIncomingCall(call, runtime.integrationId)) {
        this.coachIncomingCallSubject.next({
          call,
          integrationId: runtime.integrationId,
          consumerId: this.activeCoachConsumerId!
        });
        return;
      }
      this.ownerIncomingCallSubject.next({ call, integrationId: runtime.integrationId, device });
      },

      error: (error: any) => {
      if (runtime.device !== device) return;
      this.exceptions.record(error, { source: 'softphone', operation: 'sdk-device-callback', integrationId: runtime.integrationId });
      runtime.status = 'error';
      runtime.error = this.getSoftphoneErrorMessage(error, 'Soft phone connection was interrupted.');
      runtime.lastSeenAt = Date.now();
      this.runtimeLifecycleStatus = 'error';
      this.deviceEventSubject.next({ kind: 'error', integrationId: runtime.integrationId, device, error });
      if (this.sessionCoordinator.isOwner && this.browserSession.isActive && !this.isTerminalSoftphoneAuthFailure(error)) {
        this.scheduleRuntimeRetry(runtime);
      }
      else this.publishDerivedRuntimeState();
      void this.publishOwnerRuntimeSnapshot('connection-change');
      },

      tokenWillExpire: () => {
        if (runtime.device !== device) return;
        void this.refreshManagedRuntime(runtime.integrationId);
      }
    };

    runtime.handlers = handlers;
    device.on('registered', handlers.registered);
    device.on('unregistered', handlers.unregistered);
    device.on('incoming', handlers.incoming);
    device.on('error', handlers.error);
    device.on('tokenWillExpire', handlers.tokenWillExpire);
  }

  private unbindManagedRuntimeEvents(runtime: SoftphoneManagedRuntime, device: Device) {
    const handlers = runtime.handlers;
    if (!handlers || handlers.device !== device) return;
    try { device.off('registered', handlers.registered); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'unbind-registered-cleanup', integrationId: runtime.integrationId }); }
    try { device.off('unregistered', handlers.unregistered); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'unbind-unregistered-cleanup', integrationId: runtime.integrationId }); }
    try { device.off('incoming', handlers.incoming); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'unbind-incoming-cleanup', integrationId: runtime.integrationId }); }
    try { device.off('error', handlers.error); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'unbind-error-cleanup', integrationId: runtime.integrationId }); }
    try { device.off('tokenWillExpire', handlers.tokenWillExpire); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'unbind-token-cleanup', integrationId: runtime.integrationId }); }
    runtime.handlers = undefined;
  }

  private scheduleRuntimeRetry(runtime: SoftphoneManagedRuntime) {
    if (!this.browserSession.isActive || runtime.authorized === false) return;
    if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
    const retryDelay = this.getSoftphoneRetryDelayMs(runtime.retryAttempt);
    const activeCallUsesRuntime = this.managedRuntimeHasCall(runtime);
    const delay = activeCallUsesRuntime
      ? Math.max(retryDelay, this.callSignalingReconnectMs)
      : retryDelay;
    runtime.retryAttempt += 1;
    runtime.retryAt = Date.now() + delay;
    runtime.status = 'retrying';
    this.runtimeLifecycleStatus = 'recovering';
    this.publishDerivedRuntimeState();
    runtime.retryTimeout = setTimeout(() => {
      runtime.retryTimeout = undefined;
      const forceRecreate = !this.managedRuntimeHasCall(runtime);
      void this.refreshManagedRuntime(runtime.integrationId, forceRecreate);
    }, delay);
  }

  private managedRuntimeHasCall(runtime: SoftphoneManagedRuntime) {
    const calls = (runtime.device as any)?.calls;
    return !!runtime.device?.isBusy
      || (Array.isArray(calls) && calls.length > 0)
      || this.ownerActiveCallPayload?.integrationId === runtime.integrationId;
  }

  private async refreshManagedRuntime(integrationId: string, forceRecreate = false) {
    const runtime = this.runtimeByIntegrationId.get(integrationId);
    if (!runtime || runtime.authorized === false) return;
    const generation = this.ownerBootstrapGeneration;
    const sessionId = this.browserSession.sessionId;
    const isCurrent = () => this.sessionCoordinator.isOwner
      && generation === this.ownerBootstrapGeneration
      && this.browserSession.ownsSession(sessionId)
      && this.runtimeByIntegrationId.get(integrationId) === runtime;
    try {
      if (await this.ensureManagedRuntimeConnected(runtime, forceRecreate) === false) return;
      if (!isCurrent()) return;
      this.runtimeLifecycleStatus = 'active';
      this.publishDerivedRuntimeState();
      void this.publishOwnerRuntimeSnapshot(forceRecreate ? 'reconnect' : 'token-refresh');
    } catch (error: any) {
      if (!isCurrent()) return;
      this.exceptions.record(error, { source: 'softphone', operation: 'refresh-runtime', integrationId });
      runtime.status = 'error';
      runtime.error = this.getSoftphoneErrorMessage(error, 'Failed to refresh runtime');
      const code = this.getSoftphoneErrorCode(error);
      if (code === 'SOFTPHONE_INTEGRATION_UNAVAILABLE' || code === 'SOFTPHONE_INTEGRATION_NOT_RESOLVED') {
        await this.retireRejectedRuntime(runtime);
        if (!this.sessionCoordinator.isOwner || generation !== this.ownerBootstrapGeneration
          || !this.browserSession.ownsSession(sessionId)) return;
        this.syncSelectedDeviceReference();
        this.publishDerivedRuntimeState();
        this.requestIntegrationReconciliation();
      }
      else if (this.isTerminalSoftphoneAuthFailure(error)) this.publishDerivedRuntimeState();
      else this.scheduleRuntimeRetry(runtime);
      void this.publishOwnerRuntimeSnapshot('connection-change');
    }
  }

  private async retireRejectedRuntime(runtime: SoftphoneManagedRuntime) {
    if (this.runtimeByIntegrationId.get(runtime.integrationId) !== runtime) return;
    runtime.authorized = false;
    this.stopTokenRefreshTimer(runtime);
    if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
    runtime.retryTimeout = undefined;
    runtime.retryAt = undefined;
    if (this.managedRuntimeHasCall(runtime)) return;
    await this.destroyManagedRuntime(runtime);
    if (this.runtimeByIntegrationId.get(runtime.integrationId) === runtime) {
      this.runtimeByIntegrationId.delete(runtime.integrationId);
    }
  }

  private isTerminalSoftphoneAuthFailure(error: any) {
    return ['SOFTPHONE_PROVIDER_AUTH_FAILED', 'SOFTPHONE_INTEGRATION_UNAVAILABLE', 'SOFTPHONE_INTEGRATION_NOT_RESOLVED']
      .includes(this.getSoftphoneErrorCode(error) || '');
  }

  private getSoftphoneErrorCode(error: any) {
    return typeof error?.error?.code === 'string'
      ? error.error.code
      : typeof error?.code === 'string'
        ? error.code
        : undefined;
  }

  private getSoftphoneErrorMessage(error: any, fallback: string) {
    const code = this.getSoftphoneErrorCode(error);
    const status = Number(error?.status);
    if (code === 'SOFTPHONE_PROVIDER_AUTH_FAILED') return 'Twilio credentials were rejected. Contact an admin.';
    if (status === 0) return 'Soft phone could not reach the call service. Check your connection and try again.';
    if (status === 403) return 'You do not have permission to use this soft phone connection.';
    if (status >= 500) return 'The call service is temporarily unavailable. Please try again.';
    return fallback;
  }

  private async destroyManagedRuntime(runtime: SoftphoneManagedRuntime) {
    this.stopTokenRefreshTimer(runtime);
    if (runtime.retryTimeout) clearTimeout(runtime.retryTimeout);
    runtime.retryTimeout = undefined;
    if (runtime.device) this.unbindManagedRuntimeEvents(runtime, runtime.device);
    try { runtime.device?.disconnectAll?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'disconnect-cleanup', integrationId: runtime.integrationId }); }
    try { runtime.device?.destroy?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'device-destroy-cleanup', integrationId: runtime.integrationId }); }
    runtime.device = undefined;
  }

  private publishDerivedRuntimeState() {
    const runtimes = Array.from(this.runtimeByIntegrationId.values()).filter(runtime => runtime.authorized !== false);
    const connectedCount = runtimes.filter(runtime => runtime.status === 'connected').length;
    const partialWarning = connectedCount > 0 && connectedCount < runtimes.length;
    let state: SoftphoneState = 'inactive';
    let detail = this.setupIssue && !this.ownerActiveCallPayload ? 'Setup required' : 'Soft phone is off';
    if (runtimes.some(runtime => runtime.status === 'connected') && this.runtimeSnapshotConfirmationFailed) {
      state = 'error';
      detail = 'Transfers will not ring until VOXR confirms the softphone connection.';
    } else if (runtimes.some(runtime => runtime.status === 'connected') && !this.runtimeSnapshotConfirmed) {
      state = 'loading';
      detail = 'Confirming soft phone transfer readiness...';
    } else if (runtimes.some(runtime => runtime.status === 'connected') && this.microphoneBlocked) {
      // Readiness only; the runtime stays connected so no retry or device recreation starts.
      state = 'error';
      detail = SOFTPHONE_MICROPHONE_BLOCKED_DETAIL;
    } else if (runtimes.some(runtime => runtime.status === 'connected')) {
      state = 'ready';
      detail = partialWarning ? 'Soft phone is partially connected' : 'Ready for transfer calls';
    } else if (runtimes.some(runtime => runtime.status === 'connecting' || runtime.status === 'retrying')) {
      state = 'loading';
      detail = 'Connecting soft phone...';
    } else if (runtimes.some(runtime => runtime.status === 'error')) {
      state = 'error';
      detail = runtimes.find(runtime => runtime.error)?.error || 'Soft phone error';
    }

    this.softphoneStateSubject.next(state);
    this.softphoneStateDetailSubject.next(detail);
    this.sessionCoordinator.publishRuntimeState({
      state,
      detail,
      microphoneBlocked: this.microphoneBlocked,
      setupIssue: runtimes.length || this.ownerActiveCallPayload ? undefined : this.setupIssue,
      ownerLifecycleStatus: this.runtimeLifecycleStatus,
      bootstrapStartedAt: this.runtimeBootstrapStartedAt,
      hasPartialConnectionWarning: partialWarning,
      selectedIntegrationId: this.selectedIntegrationId(),
      hasMoreIntegrations: this.hasMoreIntegrations,
      lastSnapshotPublishedAt: this.runtimeLastSnapshotPublishedAt,
      lastSnapshotReason: this.runtimeLastSnapshotReason,
      connections: runtimes.map(runtime => ({
        integrationId: runtime.integrationId,
        accountSid: runtime.accountSid,
        title: runtime.title,
        status: runtime.status,
        deviceSid: runtime.deviceSid,
        error: runtime.error,
        lastSeenAt: runtime.lastSeenAt,
        retryAt: runtime.retryAt,
      })),
    });
  }

  private buildOwnerSnapshotConnections(): NonNullable<UpdateSoftphoneStatusRequestDto['connections']> {
    return Array.from(this.runtimeByIntegrationId.values()).filter(runtime => runtime.authorized !== false).map(runtime => ({
      integrationId: runtime.integrationId,
      status: this.mapRuntimeStatusToSnapshotStatus(runtime.status),
      deviceSid: runtime.deviceSid,
      error: runtime.error,
    }));
  }

  private setOwnerActiveCallPayload(next: SoftphoneOwnedActiveCallPayload) {
    if (next && this.pendingTerminationObservation && !this.sameTerminationLeg(this.pendingTerminationObservation, next)) {
      this.pendingTerminationObservation = undefined;
    }
    this.ownerActiveCallPayload = next;
  }

  private sameTerminationLeg(
    first: Pick<NonNullable<UpdateSoftphoneStatusRequestDto['terminationObservation']>, 'callId' | 'transferId' | 'callSid'>,
    second: Pick<NonNullable<UpdateSoftphoneStatusRequestDto['terminationObservation']>, 'callId' | 'transferId' | 'callSid'>,
  ) {
    return first.callId === second.callId
      && (first.transferId || undefined) === (second.transferId || undefined)
      && (first.callSid || undefined) === (second.callSid || undefined);
  }

  private publishRuntimeSnapshot(
    reason: Exclude<SoftphoneSnapshotReason, 'incoming-transfer-change'>,
    enabled = true
  ) {
    const browserSessionId = this.browserSession.sessionId;
    if (!this.sessionCoordinator.isOwner || !browserSessionId) return Promise.resolve();
    if (reason !== 'heartbeat' && this.runtimeByIntegrationId.size) {
      this.runtimeSnapshotConfirmed = false;
      this.publishDerivedRuntimeState();
    }
    const generation = this.runtimeSnapshotQueueGeneration;
    const runtimeSnapshotVersion = Math.max(Date.now(), this.lastRuntimeSnapshotVersion + 1);
    this.lastRuntimeSnapshotVersion = runtimeSnapshotVersion;
    const payload: UpdateSoftphoneStatusRequestDto = {
      runtimeSnapshotVersion,
      enabled,
      connections: this.buildOwnerSnapshotConnections(),
      activeCall: this.ownerActiveCallPayload ? { ...this.ownerActiveCallPayload } : null,
      ...(this.microphoneBlocked ? { microphoneBlocked: true } : {}),
    };
    const publish = this.runtimeSnapshotQueue.then(async () => {
      if (generation !== this.runtimeSnapshotQueueGeneration || !this.sessionCoordinator.isOwner || !this.browserSession.ownsSession(browserSessionId)) return;
      const terminationObservation = this.scheduledTerminationObservation
        ? undefined
        : this.pendingTerminationObservation;
      if (terminationObservation) {
        this.scheduledTerminationObservation = terminationObservation;
        payload.terminationObservation = { ...terminationObservation };
      }
      try {
        await this.userService.updateSoftphoneStatus(payload, browserSessionId);
      } catch (error) {
        if (generation === this.runtimeSnapshotQueueGeneration && this.sessionCoordinator.isOwner && this.browserSession.ownsSession(browserSessionId)) {
          if (terminationObservation && this.pendingTerminationObservation === terminationObservation
            && Number((error as any)?.status) === 409
            && this.getSoftphoneErrorCode(error) === 'SOFTPHONE_TERMINATION_OBSERVATION_STALE') {
            this.pendingTerminationObservation = undefined;
          }
          this.exceptions.record(error, { source: 'softphone', operation: 'publish-runtime-snapshot', callId: payload.activeCall?.callId, integrationId: payload.activeCall?.integrationId });
          this.runtimeSnapshotConfirmed = false;
          this.runtimeSnapshotConfirmationFailed = true;
          if (this.runtimeByIntegrationId.size) {
            this.publishSoftphoneState('error', 'Transfers will not ring until VOXR confirms the softphone connection.');
          }
          this.browserSession.handleSessionConflict(error);
        }
        throw error;
      } finally {
        if (terminationObservation && this.scheduledTerminationObservation === terminationObservation) {
          this.scheduledTerminationObservation = undefined;
        }
      }
      if (terminationObservation && this.pendingTerminationObservation === terminationObservation) {
        this.pendingTerminationObservation = undefined;
      }
      if (generation === this.runtimeSnapshotQueueGeneration && this.sessionCoordinator.isOwner && this.browserSession.ownsSession(browserSessionId) && runtimeSnapshotVersion === this.lastRuntimeSnapshotVersion) {
        this.runtimeSnapshotConfirmed = true;
        this.runtimeSnapshotConfirmationFailed = false;
        this.noteMirroredSnapshot(reason);
        this.publishDerivedRuntimeState();
      }
    });
    this.runtimeSnapshotQueue = publish.catch(() => undefined);
    return publish;
  }

  private noteMirroredSnapshot(reason: SoftphoneSnapshotReason) {
    this.runtimeLastSnapshotPublishedAt = Date.now();
    this.runtimeLastSnapshotReason = reason;
    this.sessionCoordinator.publishRuntimeState({
      lastSnapshotPublishedAt: this.runtimeLastSnapshotPublishedAt,
      lastSnapshotReason: reason,
    });
  }

  private startRuntimeSnapshotHeartbeat() {
    this.stopRuntimeSnapshotHeartbeat();
    this.runtimeSnapshotHeartbeat = setInterval(() => {
      this.requestIntegrationReconciliation();
    }, 60_000 + Math.floor(Math.random() * 5_000));
  }

  checkSetup(): Promise<boolean> {
    if (this.ownerActiveCallPayload) return Promise.resolve(true);
    return this.requestIntegrationReconciliation(false);
  }

  private requestIntegrationReconciliation(queueFollowUp = true): Promise<boolean> {
    if (!this.sessionCoordinator.isOwner || !this.browserSession.isActive || !this.currentUserId) return Promise.resolve(false);
    if (this.integrationReconciliation) {
      if (queueFollowUp) this.reconciliationRequested = true;
      return this.integrationReconciliation;
    }
    const generation = this.ownerBootstrapGeneration;
    const sessionId = this.browserSession.sessionId;
    const workspaceId = this.setupWorkspaceId;
    this.integrationReconciliation = (async () => {
      try {
        do {
          this.reconciliationRequested = false;
          const state = await this.userService.getSoftphoneState();
          if (!this.sessionCoordinator.isOwner || generation !== this.ownerBootstrapGeneration || !this.browserSession.ownsSession(sessionId) || workspaceId !== this.setupWorkspaceId) return false;
          if (!state.enabled) {
            await this.destroySharedSoftphoneDevice(false);
            this.publishDisabledRuntimeState();
            return true;
          }
          const selectedIntegrationId = this.selectPreferredSoftphoneIntegrationId({
            currentSelectedIntegrationId: this.selectedIntegrationId(),
            storedWorkspaceIntegrationId: this.getStoredWorkspaceSelectedIntegration(),
            resolvedIntegrations: state.resolvedIntegrations,
          });
          if (selectedIntegrationId) this.setSelectedIntegrationId(selectedIntegrationId);
          else {
            this.clearStoredWorkspaceSelectedIntegration();
            this.applySelectedIntegrationId(undefined);
          }
          this.hasMoreIntegrations = state.resolvedIntegrations.length > 1;
          const authoritativeFailure = await this.syncResolvedIntegrations(state.resolvedIntegrations, false, sessionId);
          if (!this.sessionCoordinator.isOwner || generation !== this.ownerBootstrapGeneration || !this.browserSession.ownsSession(sessionId) || workspaceId !== this.setupWorkspaceId) return false;
          if (authoritativeFailure) throw new Error('Authoritative softphone integration reconciliation failed.');
          this.setupIssue = state.setupIssue;
          this.reconciliationRetryAttempt = 0;
          this.cancelIntegrationReconciliationRetry();
          this.syncSelectedDeviceReference();
          this.publishDerivedRuntimeState();
          await this.publishOwnerRuntimeSnapshot('heartbeat');
        } while (this.reconciliationRequested);
        return true;
      } catch (error) {
        if (this.sessionCoordinator.isOwner && generation === this.ownerBootstrapGeneration && this.browserSession.ownsSession(sessionId)) {
          this.exceptions.record(error, { source: 'softphone', operation: 'integration-reconciliation' });
          this.scheduleIntegrationReconciliationRetry();
        }
        return false;
      } finally {
        this.integrationReconciliation = undefined;
        if (this.reconciliationRequested) this.requestIntegrationReconciliation();
      }
    })();
    return this.integrationReconciliation;
  }

  private scheduleIntegrationReconciliationRetry() {
    if (!this.sessionCoordinator.isOwner || !this.browserSession.isActive) return;
    this.cancelIntegrationReconciliationRetry();
    const delays = [5_000, 15_000, 30_000, 60_000];
    const delay = delays[Math.min(this.reconciliationRetryAttempt, delays.length - 1)];
    this.reconciliationRetryAttempt += 1;
    this.reconciliationRetryTimeout = setTimeout(() => {
      this.reconciliationRetryTimeout = undefined;
      this.requestIntegrationReconciliation();
    }, delay);
  }

  private cancelIntegrationReconciliationRetry() {
    if (this.reconciliationRetryTimeout) clearTimeout(this.reconciliationRetryTimeout);
    this.reconciliationRetryTimeout = undefined;
  }

  private stopRuntimeSnapshotHeartbeat() {
    if (this.runtimeSnapshotHeartbeat) clearInterval(this.runtimeSnapshotHeartbeat);
    this.runtimeSnapshotHeartbeat = undefined;
  }
}
